import { Seo } from "@/components/Seo";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { COMPANY } from "@/lib/site";
import { ArrowUpRight, MapPin, Building, Users, Award } from "lucide-react";

const VALUE_ICONS = [Award, Users, Building, MapPin];

export default function ONamaPage() {
  const { t } = useTranslation();
  const story = t("oNama.story", { returnObjects: true });
  const values = t("oNama.values", { returnObjects: true });
  const team = t("oNama.team", { returnObjects: true });
  const storyArr = Array.isArray(story) ? story : [];
  const valuesArr = Array.isArray(values) ? values : [];
  const teamArr = Array.isArray(team) ? team : [];

  return (
    <div data-testid="o-nama-page">
      <Seo title={t("oNama.seoTitle")} path="/o-nama" description={t("oNama.seoDesc")} />
      {/* HERO */}
      <section className="border-b border-hair">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-20 pb-16 lg:pt-28 lg:pb-20">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-8">
              <div className="overline text-ink-soft flex items-center gap-3">
                <span className="w-8 h-px bg-ink-soft/40" /> {t("oNama.kicker")}
              </div>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] font-bold text-ink mt-6">
                {t("oNama.heroTitle1")}<br />
                <span className="text-ink/40">{t("oNama.heroTitle2")}</span><br />
                {t("oNama.heroTitle3")}
              </h1>
            </div>
            <div
              className="md:col-span-4 text-ink-soft leading-relaxed [&_b]:text-ink [&_b]:font-medium"
              dangerouslySetInnerHTML={{ __html: t("oNama.heroLeadHtml", { company: COMPANY.name }) }}
            />
          </div>
        </div>
      </section>

      {/* STORY + IMAGE bento */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-7 border border-hair p-8 lg:p-12 bg-white">
            <div className="overline text-ink-soft">{t("oNama.storyKicker")}</div>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-semibold text-ink mt-4 leading-tight">
              {t("oNama.storyTitle")}
            </h2>
            <div className="text-ink-soft mt-6 leading-relaxed space-y-4">
              {storyArr.map((p, k) => <p key={k}>{p}</p>)}
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="relative aspect-[4/5] overflow-hidden border border-hair">
              <img
                src="https://images.unsplash.com/photo-1598368195835-91e67f80c9d7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxhcmNoaXRlY3QlMjBkZXNrJTIwcGxhbnN8ZW58MHx8fHwxNzgyNDY1NzEzfDA&ixlib=rb-4.1.0&q=85"
                alt={t("oNama.storyImgAlt")}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="surface border-t border-hair">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-6">
              <div className="overline text-ink-soft">{t("oNama.valuesKicker")}</div>
              <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-semibold text-ink mt-4 leading-[1.05]">
                {t("oNama.valuesTitle")}
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-ink/10 mt-12 border border-ink/10">
            {valuesArr.map((v, i) => {
              const Icon = VALUE_ICONS[i] || Award;
              return (
                <div key={v.t} className="bg-white p-8 hover-lift">
                  <Icon className="w-6 h-6 text-ink" strokeWidth={1.5} />
                  <h3 className="font-display text-xl font-semibold text-ink mt-6">{v.t}</h3>
                  <p className="text-ink-soft text-sm leading-relaxed mt-2">{v.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28 border-t border-hair">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-6">
            <div className="overline text-ink-soft">{t("oNama.teamKicker")}</div>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-semibold text-ink mt-4 leading-[1.05]">
              {t("oNama.teamTitle")}
            </h2>
          </div>
          <div className="md:col-span-6 text-ink-soft leading-relaxed">
            {t("oNama.teamIntro")}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12">
          {teamArr.map((m, i) => (
            <div key={m.name} className="border border-hair p-6 bg-white hover-lift">
              <div className="aspect-square bg-ink/5 flex items-center justify-center">
                <span className="font-display text-6xl text-ink/30 font-bold">
                  {m.name.split(" ").map((n) => n[0]).join("")}
                </span>
              </div>
              <div className="mt-5">
                <div className="overline text-ink-soft">0{i + 1}</div>
                <div className="font-display font-semibold text-ink mt-2">{m.name}</div>
                <div className="text-ink-soft text-sm">{m.role}</div>
                {m.years && <div className="text-ink-soft text-xs mt-1">{t("oNama.experienceLabel")} · {m.years}</div>}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CONTACT BOX */}
      <section className="border-t border-hair bg-ink text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <div className="overline text-white/60">{t("oNama.officeKicker")}</div>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-semibold mt-3 leading-tight">
              {t("oNama.officeTitle", { address: COMPANY.address })}
            </h2>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link
              to="/prijava"
              data-testid="o-nama-cta"
              className="inline-flex items-center gap-2 bg-white text-ink px-6 py-3.5 text-sm font-medium rounded-sm hover:bg-white/90 transition-colors"
            >
              {t("common.applyNow")}
              <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
